// @ts-nocheck — Deeply-nested react-hook-form generics over the SiteContent document
// make the field-array typings unwieldy; suppressed to keep the admin build green,
// matching SettingsPage. Runtime is unaffected. TODO(owner): restore type-safety.
/**
 * Site Content Page (Phase B3)
 *
 * Full editor for the artist site-content document (`pageContent`). The whole
 * document is held in a single react-hook-form instance and PATCHed back to
 * `/api/v1/easel/site/config` as `{ pageContent }` — the backend stores it
 * whole in the page_content JSONB column.
 *
 * Mirrors the pattern in SettingsPage.tsx (useSiteConfig / useUpdateSiteConfig,
 * react-hook-form, tabs, form.reset(config) in an effect, onSubmit → mutateAsync).
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowLeft,
  Home,
  User,
  Navigation,
  Store,
  Share2,
  Loader2,
  Save,
  Plus,
  Trash2,
  Palette,
  Wand2,
} from 'lucide-react'
import { useSiteConfig, useUpdateSiteConfig } from '../hooks'
import { EaselImageField } from '../components/EaselImageField'
import type { SiteContent } from '../types'

// =============================================================================
// Empty document — what a freshly-provisioned artist (no pageContent) gets so
// the form always renders with a complete, editable structure.
// =============================================================================

const emptySiteContent: SiteContent = {
  identity: { siteName: '', logoText: '', tagline: '', footerBlurb: '' },
  hero: {
    headline: '',
    headlineAccent: '',
    subcopy: '',
    pullQuote: '',
    ctas: [],
    image: { url: '', alt: '' },
    imageCaption: { title: '', meta: '', href: '' },
  },
  homeQuote: '',
  about: {
    eyebrow: '',
    heading: '',
    headingAccent: '',
    bio: [],
    pullQuote: '',
    portrait: { url: '', alt: '' },
    portraitCaption: '',
    milestones: [],
  },
  shopHub: { heading: '', subheading: '', cards: [] },
  nav: { items: [], contactLabel: '', contactHref: '' },
  footer: {
    columns: [],
    copyrightName: '',
    newsletter: { heading: '', blurb: '' },
  },
  social: [],
  theme: {
    palette: {
      primary: '#68315a',
      primaryLight: '#b465a5',
      accent: '#d4af37',
      background: '#fdfbf7',
      ink: '#2D2D2D',
    },
    fonts: {
      display: 'Playfair Display',
      body: 'Lato',
    },
  },
}

/**
 * Deep-merge a (possibly partial / null) stored document onto the empty
 * skeleton so every field the form registers is defined. Arrays are taken
 * wholesale from the stored value when present.
 */
function toFormValues(pageContent?: SiteContent | null): SiteContent {
  const pc = pageContent || ({} as Partial<SiteContent>)
  return {
    identity: { ...emptySiteContent.identity, ...(pc.identity || {}) },
    hero: {
      ...emptySiteContent.hero,
      ...(pc.hero || {}),
      image: { ...emptySiteContent.hero.image, ...(pc.hero?.image || {}) },
      imageCaption: {
        ...emptySiteContent.hero.imageCaption,
        ...(pc.hero?.imageCaption || {}),
      },
      ctas: pc.hero?.ctas ?? [],
    },
    homeQuote: pc.homeQuote ?? '',
    about: {
      ...emptySiteContent.about,
      ...(pc.about || {}),
      portrait: { ...emptySiteContent.about.portrait, ...(pc.about?.portrait || {}) },
      bio: pc.about?.bio ?? [],
      milestones: pc.about?.milestones ?? [],
    },
    shopHub: {
      ...emptySiteContent.shopHub,
      ...(pc.shopHub || {}),
      cards: pc.shopHub?.cards ?? [],
    },
    nav: {
      ...emptySiteContent.nav,
      ...(pc.nav || {}),
      items: pc.nav?.items ?? [],
    },
    footer: {
      ...emptySiteContent.footer,
      ...(pc.footer || {}),
      newsletter: {
        ...emptySiteContent.footer.newsletter,
        ...(pc.footer?.newsletter || {}),
      },
      columns: pc.footer?.columns ?? [],
    },
    social: pc.social ?? [],
    theme: {
      palette: {
        ...emptySiteContent.theme.palette,
        ...(pc.theme?.palette || {}),
      },
      fonts: {
        ...emptySiteContent.theme.fonts,
        ...(pc.theme?.fonts || {}),
      },
    },
  }
}

// =============================================================================
// Color helpers for the Theme tab — hex ↔ HSL (h in [0,360), s/l in [0,1]).
// =============================================================================

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  let clean = (hex || '').replace('#', '').trim()
  if (clean.length === 3) {
    clean = clean
      .split('')
      .map((c) => c + c)
      .join('')
  }
  if (clean.length !== 6 || /[^0-9a-fA-F]/.test(clean)) {
    return { h: 0, s: 0, l: 0 }
  }
  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  const l = (max + min) / 2
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  return { h, s, l }
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360
  s = Math.min(1, Math.max(0, s))
  l = Math.min(1, Math.max(0, l))
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Derive a pleasant palette from the primary color using simple HSL math.
 * Deterministic — same primary always yields the same palette.
 *   primaryLight → same hue, higher lightness (~62%), slightly softer saturation
 *   accent       → +40° hue shift at mid lightness (~52%)
 *   background   → same hue, very high lightness (~97%), low saturation
 *   ink          → same hue, near-neutral, very low lightness (~17%)
 */
function derivePalette(primary: string) {
  const { h, s } = hexToHsl(primary)
  return {
    primaryLight: hslToHex(h, Math.min(s, 0.6), 0.62),
    accent: hslToHex(h + 40, Math.max(0.45, Math.min(s, 0.7)), 0.52),
    background: hslToHex(h, Math.min(s, 0.35), 0.97),
    ink: hslToHex(h, Math.min(s, 0.15), 0.17),
  }
}

const DISPLAY_FONTS = [
  'Playfair Display',
  'Cormorant Garamond',
  'Libre Baskerville',
  'Lora',
  'EB Garamond',
]

const BODY_FONTS = ['Lato', 'Inter', 'Work Sans', 'Nunito Sans', 'Source Sans 3']

// =============================================================================
// Small field helpers
// =============================================================================

function TextField({ register, name, label, placeholder, description }: any) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input {...register(name)} placeholder={placeholder} />
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  )
}

function AreaField({ register, name, label, placeholder, rows = 3, description }: any) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Textarea {...register(name)} placeholder={placeholder} rows={rows} />
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  )
}

function ArrayHeader({ title, description, onAdd, addLabel = 'Add' }: any) {
  return (
    <div className="flex items-start justify-between">
      <div className="space-y-0.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onAdd}>
        <Plus className="h-4 w-4 mr-1" />
        {addLabel}
      </Button>
    </div>
  )
}

function RowShell({ children, onRemove }: any) {
  return (
    <div className="rounded-lg border p-4 space-y-3 relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
      {children}
    </div>
  )
}

// =============================================================================
// Theme — a single color row: a native color picker + a hex text input, both
// bound to the same form field so either control updates the value.
// =============================================================================

function ColorRow({ control, name, label }: any) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const isValidHex = /^#[0-9a-fA-F]{6}$/.test(field.value || '')
        return (
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={isValidHex ? field.value : '#000000'}
              onChange={(e) => field.onChange(e.target.value)}
              className="h-9 w-12 shrink-0 cursor-pointer rounded border bg-transparent p-0.5"
              aria-label={`${label} color picker`}
            />
            <div className="w-32">
              <Label className="text-xs text-muted-foreground">{label}</Label>
            </div>
            <Input
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value)}
              placeholder="#000000"
              className="w-36 font-mono"
            />
          </div>
        )
      }}
    />
  )
}

// =============================================================================
// Nested footer-column links editor (a field array inside each footer column)
// =============================================================================

function FooterColumnLinks({ control, register, columnIndex }: any) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `footer.columns.${columnIndex}.links`,
  })

  return (
    <div className="space-y-2 pl-3 border-l">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Links</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7"
          onClick={() => append({ label: '', href: '' })}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add link
        </Button>
      </div>
      {fields.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No links yet.</p>
      )}
      {fields.map((field, i) => (
        <div key={field.id} className="flex items-center gap-2">
          <Input
            {...register(`footer.columns.${columnIndex}.links.${i}.label`)}
            placeholder="Label"
            className="flex-1"
          />
          <Input
            {...register(`footer.columns.${columnIndex}.links.${i}.href`)}
            placeholder="/href"
            className="flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
            onClick={() => remove(i)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

export function SiteContentPage() {
  const navigate = useNavigate()
  const { data: config, isLoading } = useSiteConfig()
  const updateConfig = useUpdateSiteConfig()

  const form = useForm<SiteContent>({
    defaultValues: emptySiteContent,
  })
  const { register, control } = form

  // Field arrays
  const ctas = useFieldArray({ control, name: 'hero.ctas' })
  const bio = useFieldArray({ control, name: 'about.bio' })
  const milestones = useFieldArray({ control, name: 'about.milestones' })
  const cards = useFieldArray({ control, name: 'shopHub.cards' })
  const navItems = useFieldArray({ control, name: 'nav.items' })
  const columns = useFieldArray({ control, name: 'footer.columns' })
  const social = useFieldArray({ control, name: 'social' })

  useEffect(() => {
    if (config) {
      form.reset(toFormValues(config.pageContent))
    }
  }, [config, form])

  const onSubmit = async (data: SiteContent) => {
    await updateConfig.mutateAsync({ pageContent: data })
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-8">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <Card>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Site Content</h1>
            <p className="text-muted-foreground">
              Edit the copy, imagery and links that appear across your public site
            </p>
          </div>
        </div>
        <Button onClick={form.handleSubmit(onSubmit)} disabled={updateConfig.isPending}>
          {updateConfig.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Tabs defaultValue="homepage" className="space-y-6">
          <TabsList>
            <TabsTrigger value="homepage">
              <Home className="h-4 w-4 mr-2" />
              Homepage
            </TabsTrigger>
            <TabsTrigger value="about">
              <User className="h-4 w-4 mr-2" />
              About
            </TabsTrigger>
            <TabsTrigger value="navfooter">
              <Navigation className="h-4 w-4 mr-2" />
              Navigation &amp; Footer
            </TabsTrigger>
            <TabsTrigger value="identity">
              <Store className="h-4 w-4 mr-2" />
              Identity
            </TabsTrigger>
            <TabsTrigger value="social">
              <Share2 className="h-4 w-4 mr-2" />
              Social
            </TabsTrigger>
            <TabsTrigger value="theme">
              <Palette className="h-4 w-4 mr-2" />
              Theme
            </TabsTrigger>
          </TabsList>

          {/* ================================================================= */}
          {/* HOMEPAGE                                                          */}
          {/* ================================================================= */}
          <TabsContent value="homepage" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Hero</CardTitle>
                <CardDescription>The top of your homepage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="hero.headline" label="Headline" placeholder="Where light meets" />
                  <TextField register={register} name="hero.headlineAccent" label="Headline Accent" placeholder="the canvas" />
                </div>
                <AreaField register={register} name="hero.subcopy" label="Subcopy" placeholder="A short introduction..." rows={3} />
                <AreaField register={register} name="hero.pullQuote" label="Pull Quote" placeholder="A memorable line..." rows={2} />

                <Separator />

                {/* Hero CTAs */}
                <div className="space-y-3">
                  <ArrayHeader
                    title="Call-to-Action Buttons"
                    description="Buttons shown in the hero"
                    addLabel="Add CTA"
                    onAdd={() => ctas.append({ label: '', href: '', style: 'primary' })}
                  />
                  {ctas.fields.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">No CTAs yet.</p>
                  )}
                  {ctas.fields.map((field, i) => (
                    <RowShell key={field.id} onRemove={() => ctas.remove(i)}>
                      <div className="grid gap-3 md:grid-cols-3 pr-8">
                        <div className="space-y-1.5">
                          <Label>Label</Label>
                          <Input {...register(`hero.ctas.${i}.label`)} placeholder="Explore Work" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Href</Label>
                          <Input {...register(`hero.ctas.${i}.href`)} placeholder="/collections" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Style</Label>
                          <Controller
                            control={control}
                            name={`hero.ctas.${i}.style`}
                            render={({ field: f }) => (
                              <Select value={f.value} onValueChange={f.onChange}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Style" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="primary">Primary</SelectItem>
                                  <SelectItem value="outline">Outline</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>
                      </div>
                    </RowShell>
                  ))}
                </div>

                <Separator />

                {/* Hero image + caption */}
                <div className="space-y-1.5">
                  <Label>Hero Image</Label>
                  <Controller
                    control={control}
                    name="hero.image.url"
                    render={({ field }) => (
                      <EaselImageField
                        kind="site"
                        aspectHint="16:9"
                        value={field.value}
                        alt={form.watch('hero.image.alt')}
                        onChange={(url, meta) => {
                          field.onChange(url)
                          if (meta && meta.alt !== undefined) {
                            form.setValue('hero.image.alt', meta.alt)
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <TextField register={register} name="hero.imageCaption.title" label="Caption Title" placeholder="Artwork title" />
                  <TextField register={register} name="hero.imageCaption.meta" label="Caption Meta" placeholder="Medium, year" />
                  <TextField register={register} name="hero.imageCaption.href" label="Caption Href" placeholder="/products/..." />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Home Quote</CardTitle>
                <CardDescription>A featured quote on the homepage</CardDescription>
              </CardHeader>
              <CardContent>
                <AreaField register={register} name="homeQuote" label="Quote" placeholder="An inspiring quote..." rows={2} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Shop Hub</CardTitle>
                <CardDescription>The shop section heading and cards</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="shopHub.heading" label="Heading" placeholder="Shop the collection" />
                  <TextField register={register} name="shopHub.subheading" label="Subheading" placeholder="Prints, originals & more" />
                </div>

                <Separator />

                <div className="space-y-3">
                  <ArrayHeader
                    title="Shop Cards"
                    description="Individual cards linking into the shop"
                    addLabel="Add Card"
                    onAdd={() =>
                      cards.append({
                        title: '',
                        description: '',
                        href: '',
                        image: { url: '', alt: '' },
                        ctaLabel: '',
                        badge: '',
                        variant: '',
                      })
                    }
                  />
                  {cards.fields.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">No cards yet.</p>
                  )}
                  {cards.fields.map((field, i) => (
                    <RowShell key={field.id} onRemove={() => cards.remove(i)}>
                      <div className="grid gap-3 md:grid-cols-2 pr-8">
                        <div className="space-y-1.5">
                          <Label>Title</Label>
                          <Input {...register(`shopHub.cards.${i}.title`)} placeholder="Original Paintings" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>CTA Label</Label>
                          <Input {...register(`shopHub.cards.${i}.ctaLabel`)} placeholder="Browse" />
                        </div>
                      </div>
                      <div className="space-y-1.5 pr-8">
                        <Label>Description</Label>
                        <Textarea {...register(`shopHub.cards.${i}.description`)} rows={2} placeholder="Short description" />
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 pr-8">
                        <div className="space-y-1.5">
                          <Label>Href</Label>
                          <Input {...register(`shopHub.cards.${i}.href`)} placeholder="/collections/originals" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Image URL</Label>
                          <Input {...register(`shopHub.cards.${i}.image.url`)} placeholder="https://..." />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Image Alt</Label>
                          <Input {...register(`shopHub.cards.${i}.image.alt`)} placeholder="Describe the image" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Badge (optional)</Label>
                          <Input {...register(`shopHub.cards.${i}.badge`)} placeholder="New" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Variant (optional)</Label>
                          <Input {...register(`shopHub.cards.${i}.variant`)} placeholder="dark" />
                        </div>
                      </div>
                    </RowShell>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================================= */}
          {/* ABOUT                                                             */}
          {/* ================================================================= */}
          <TabsContent value="about" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>About Heading</CardTitle>
                <CardDescription>Intro copy for your about section</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <TextField register={register} name="about.eyebrow" label="Eyebrow" placeholder="About the artist" />
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="about.heading" label="Heading" placeholder="A life in" />
                  <TextField register={register} name="about.headingAccent" label="Heading Accent" placeholder="color" />
                </div>
                <AreaField register={register} name="about.pullQuote" label="Pull Quote" placeholder="A memorable line..." rows={2} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Biography</CardTitle>
                <CardDescription>Each paragraph is a separate block</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ArrayHeader
                  title="Bio Paragraphs"
                  addLabel="Add Paragraph"
                  onAdd={() => bio.append('')}
                />
                {bio.fields.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No paragraphs yet.</p>
                )}
                {bio.fields.map((field, i) => (
                  <div key={field.id} className="flex items-start gap-2">
                    <Textarea
                      {...register(`about.bio.${i}`)}
                      rows={3}
                      placeholder={`Paragraph ${i + 1}`}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => bio.remove(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Portrait</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Portrait</Label>
                  <Controller
                    control={control}
                    name="about.portrait.url"
                    render={({ field }) => (
                      <EaselImageField
                        kind="site"
                        aspectHint="3:4"
                        value={field.value}
                        alt={form.watch('about.portrait.alt')}
                        onChange={(url, meta) => {
                          field.onChange(url)
                          if (meta && meta.alt !== undefined) {
                            form.setValue('about.portrait.alt', meta.alt)
                          }
                        }}
                      />
                    )}
                  />
                </div>
                <TextField register={register} name="about.portraitCaption" label="Portrait Caption" placeholder="In the studio, 2024" />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Milestones</CardTitle>
                <CardDescription>Timeline of exhibitions, awards and career moments</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ArrayHeader
                  title="Milestones"
                  addLabel="Add Milestone"
                  onAdd={() =>
                    milestones.append({ year: '', title: '', location: '', isHighlight: false })
                  }
                />
                {milestones.fields.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No milestones yet.</p>
                )}
                {milestones.fields.map((field, i) => (
                  <RowShell key={field.id} onRemove={() => milestones.remove(i)}>
                    <div className="grid gap-3 md:grid-cols-3 pr-8">
                      <div className="space-y-1.5">
                        <Label>Year</Label>
                        <Input {...register(`about.milestones.${i}.year`)} placeholder="2024" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Title</Label>
                        <Input {...register(`about.milestones.${i}.title`)} placeholder="Solo Exhibition" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Location</Label>
                        <Input {...register(`about.milestones.${i}.location`)} placeholder="New York, NY" />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm pr-8">
                      <input type="checkbox" {...register(`about.milestones.${i}.isHighlight`)} className="h-4 w-4" />
                      Highlight this milestone
                    </label>
                  </RowShell>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================================= */}
          {/* NAVIGATION & FOOTER                                               */}
          {/* ================================================================= */}
          <TabsContent value="navfooter" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Navigation</CardTitle>
                <CardDescription>Primary menu items and the contact link</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <ArrayHeader
                    title="Menu Items"
                    addLabel="Add Item"
                    onAdd={() => navItems.append({ label: '', href: '' })}
                  />
                  {navItems.fields.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">No items yet.</p>
                  )}
                  {navItems.fields.map((field, i) => (
                    <div key={field.id} className="flex items-center gap-2">
                      <Input {...register(`nav.items.${i}.label`)} placeholder="Label" className="flex-1" />
                      <Input {...register(`nav.items.${i}.href`)} placeholder="/href" className="flex-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => navItems.remove(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="nav.contactLabel" label="Contact Label" placeholder="Get in touch" />
                  <TextField register={register} name="nav.contactHref" label="Contact Href" placeholder="/contact" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Footer</CardTitle>
                <CardDescription>Columns of links, copyright and newsletter copy</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-3">
                  <ArrayHeader
                    title="Footer Columns"
                    addLabel="Add Column"
                    onAdd={() => columns.append({ heading: '', links: [] })}
                  />
                  {columns.fields.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">No columns yet.</p>
                  )}
                  {columns.fields.map((field, i) => (
                    <RowShell key={field.id} onRemove={() => columns.remove(i)}>
                      <div className="space-y-1.5 pr-8">
                        <Label>Heading</Label>
                        <Input {...register(`footer.columns.${i}.heading`)} placeholder="Explore" />
                      </div>
                      <FooterColumnLinks control={control} register={register} columnIndex={i} />
                    </RowShell>
                  ))}
                </div>

                <Separator />

                <TextField register={register} name="footer.copyrightName" label="Copyright Name" placeholder="Jane Doe Studio" />
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="footer.newsletter.heading" label="Newsletter Heading" placeholder="Stay in the loop" />
                  <TextField register={register} name="footer.newsletter.blurb" label="Newsletter Blurb" placeholder="News, drops & studio notes" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================================= */}
          {/* IDENTITY                                                          */}
          {/* ================================================================= */}
          <TabsContent value="identity" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Identity</CardTitle>
                <CardDescription>Your name, logo text and standing copy</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextField register={register} name="identity.siteName" label="Site Name" placeholder="Jane Doe" />
                  <TextField register={register} name="identity.logoText" label="Logo Text" placeholder="JD" />
                </div>
                <TextField register={register} name="identity.tagline" label="Tagline" placeholder="Contemporary abstract painter" />
                <AreaField register={register} name="identity.footerBlurb" label="Footer Blurb" placeholder="A short line shown in the footer" rows={3} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================================= */}
          {/* SOCIAL                                                            */}
          {/* ================================================================= */}
          <TabsContent value="social" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Social Links</CardTitle>
                <CardDescription>Where visitors can find you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ArrayHeader
                  title="Social Links"
                  addLabel="Add Link"
                  onAdd={() => social.append({ platform: 'instagram', url: '' })}
                />
                {social.fields.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">No links yet.</p>
                )}
                {social.fields.map((field, i) => (
                  <div key={field.id} className="flex items-end gap-2">
                    <div className="space-y-1.5 w-40">
                      <Label>Platform</Label>
                      <Controller
                        control={control}
                        name={`social.${i}.platform`}
                        render={({ field: f }) => (
                          <Select value={f.value} onValueChange={f.onChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Platform" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="instagram">Instagram</SelectItem>
                              <SelectItem value="tiktok">TikTok</SelectItem>
                              <SelectItem value="pinterest">Pinterest</SelectItem>
                              <SelectItem value="email">Email</SelectItem>
                              <SelectItem value="website">Website</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div className="space-y-1.5 flex-1">
                      <Label>URL</Label>
                      <Input {...register(`social.${i}.url`)} placeholder="https://instagram.com/..." />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 text-muted-foreground hover:text-destructive shrink-0"
                      onClick={() => social.remove(i)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ================================================================= */}
          {/* THEME                                                             */}
          {/* ================================================================= */}
          <TabsContent value="theme" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Palette</CardTitle>
                <CardDescription>
                  The colors that define your site. Pick a primary and generate a
                  matching palette, or fine-tune each color by hand.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-3">
                    <ColorRow control={control} name="theme.palette.primary" label="Primary" />
                    <ColorRow control={control} name="theme.palette.primaryLight" label="Primary Light" />
                    <ColorRow control={control} name="theme.palette.accent" label="Accent" />
                    <ColorRow control={control} name="theme.palette.background" label="Background" />
                    <ColorRow control={control} name="theme.palette.ink" label="Ink (text)" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const primary = form.getValues('theme.palette.primary')
                      const derived = derivePalette(primary)
                      form.setValue('theme.palette.primaryLight', derived.primaryLight, {
                        shouldDirty: true,
                      })
                      form.setValue('theme.palette.accent', derived.accent, {
                        shouldDirty: true,
                      })
                      form.setValue('theme.palette.background', derived.background, {
                        shouldDirty: true,
                      })
                      form.setValue('theme.palette.ink', derived.ink, {
                        shouldDirty: true,
                      })
                    }}
                  >
                    <Wand2 className="h-4 w-4 mr-2" />
                    Generate from primary
                  </Button>
                </div>

                <Separator />

                {/* Live swatch preview */}
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="flex overflow-hidden rounded-lg border">
                    {[
                      { key: 'theme.palette.primary', label: 'Primary' },
                      { key: 'theme.palette.primaryLight', label: 'Light' },
                      { key: 'theme.palette.accent', label: 'Accent' },
                      { key: 'theme.palette.background', label: 'Background' },
                      { key: 'theme.palette.ink', label: 'Ink' },
                    ].map((s) => {
                      const color = form.watch(s.key)
                      return (
                        <div key={s.key} className="flex-1">
                          <div className="h-16 w-full" style={{ backgroundColor: color }} />
                          <div className="px-2 py-1 text-center">
                            <div className="text-[10px] font-medium">{s.label}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">
                              {color}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Typography</CardTitle>
                <CardDescription>Heading and body typefaces for your site</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Display Font (headings)</Label>
                    <Controller
                      control={control}
                      name="theme.fonts.display"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a display font" />
                          </SelectTrigger>
                          <SelectContent>
                            {DISPLAY_FONTS.map((f) => (
                              <SelectItem key={f} value={f} style={{ fontFamily: f }}>
                                {f}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Body Font</Label>
                    <Controller
                      control={control}
                      name="theme.fonts.body"
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a body font" />
                          </SelectTrigger>
                          <SelectContent>
                            {BODY_FONTS.map((f) => (
                              <SelectItem key={f} value={f} style={{ fontFamily: f }}>
                                {f}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </form>
    </div>
  )
}
